import { useUser } from "@/hooks/useUser";
import { isTimesheetAccessible, isProjectBudgetAccessible } from "@/utils/paywallUtils";
import { ExtendPlanTypeEnumSchema, PlanTypeEnumSchema } from "@backend/src/schemas/enums";
import { Navigate, Outlet } from "react-router-dom";

/**
 * Features that can be gated behind a subscription check.
 *
 * - "paid"        → Requires any paid plan (BUSINESS / ENTERPRISE) or active free trial.
 *                   Blocks FREE-plan users from dashboards, risk, workload, capacity-planning.
 * - "timesheet"   → Requires ENTERPRISE or active free trial.
 * - "budget"      → Requires BUSINESS / ENTERPRISE or active free trial.
 */
export type GatedFeature = "paid" | "timesheet" | "budget";

interface SubscriptionGuardProps {
  feature: GatedFeature;
  redirectTo?: string;
}

const hasPaidAccess = (user: ReturnType<typeof useUser>["user"]): boolean => {
  if (!user) return false;

  // Enterprise or Business
  const planType = user.subscription?.planType;
  if (
    planType === PlanTypeEnumSchema.ENTERPRISE ||
    planType === PlanTypeEnumSchema.BUSINESS
  ) {
    return true;
  }

  // Active free trial
  if (user.subscription?.customerId === ExtendPlanTypeEnumSchema.FREE_TRIAL) {
    return true;
  }

  return false;
};

/**
 * Route-level guard that blocks access based on the user's subscription plan.
 */
const SubscriptionGuard = ({ feature, redirectTo = "/plans" }: SubscriptionGuardProps) => {
  const { user } = useUser();

  if (!user) {
    return null;
  }

  let hasAccess = false;

  switch (feature) {
    case "paid":
      hasAccess = hasPaidAccess(user);
      break;
    case "timesheet":
      hasAccess = isTimesheetAccessible(user);
      break;
    case "budget":
      hasAccess = isProjectBudgetAccessible(user);
      break;
    default:
      hasAccess = false;
  }

  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
};

export default SubscriptionGuard;
